/**
 * Media URL Resolver
 *
 * Handles fetching media from the Rool SDK and caching blob URLs.
 * SDK media URLs require authentication, so we use space.fetchMedia()
 * to get the blob and create a local blob URL for display.
 */

import { get } from 'svelte/store';
import type { RoolChannel } from '@rool-dev/sdk';
import { channel } from '../stores';

/** Cache of resolved blob URLs keyed by original URL */
const blobUrlCache = new Map<string, string>();

/** Track pending fetches to avoid duplicate requests */
const pendingFetches = new Map<string, Promise<string | null>>();

/**
 * Check if a URL is a Rool SDK media URL that needs authentication.
 * SDK URLs typically point to the Rool backend.
 */
export function isRoolMediaUrl(url: string): boolean {
	if (!url) return false;
	// Blob URLs and data URLs are already resolved
	if (url.startsWith('blob:') || url.startsWith('data:')) return false;
	// Check for Rool backend URLs
	return url.includes('rool.dev') || url.includes('rool-dev');
}

/**
 * Get a displayable URL for media.
 * For Rool SDK URLs, returns a cached blob URL or fetches and caches it.
 * For other URLs, returns the original URL.
 */
export async function resolveMediaUrl(url: string): Promise<string | null> {
	if (!url) return null;

	// Already a blob or data URL - return as-is
	if (url.startsWith('blob:') || url.startsWith('data:')) {
		return url;
	}

	// Check cache first
	const cached = blobUrlCache.get(url);
	if (cached) return cached;

	// Check if there's already a pending fetch for this URL
	const pending = pendingFetches.get(url);
	if (pending) return pending;

	// External URLs: return as-is (let <img> tag load directly, no CORS issues)
	// If the image fails to load, the renderer can call proxyMediaUrl() as fallback.
	if (!isRoolMediaUrl(url)) {
		return url;
	}

	// Rool SDK URLs: fetch via SDK (adds auth headers)
	const currentSpace = get(channel) as RoolChannel | null;
	if (!currentSpace) {
		console.warn('[media] No space available to fetch media');
		return null;
	}

	// Create and track the fetch promise
	const fetchPromise = (async (): Promise<string | null> => {
		try {
			const response = await currentSpace.fetchMedia(url);
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);

			// Cache the result
			blobUrlCache.set(url, blobUrl);
			return blobUrl;
		} catch (err) {
			console.error('[media] Failed to fetch media:', url, err);
			return null;
		} finally {
			pendingFetches.delete(url);
		}
	})();

	pendingFetches.set(url, fetchPromise);
	return fetchPromise;
}

/**
 * Synchronously get a cached blob URL if available.
 * Returns empty string for URLs being proxied (Rool or CORS-blocked).
 * Returns the original URL for external URLs from CORS-ok domains.
 */
export function getCachedMediaUrl(url: string): string {
	if (!url) return '';
	if (url.startsWith('blob:') || url.startsWith('data:')) return url;

	const cached = blobUrlCache.get(url);
	if (cached) return cached;

	if (!isRoolMediaUrl(url)) {
		// If we know this domain is CORS-blocked and the proxy is in flight,
		// return empty to prevent showing a placeholder image.
		const domain = getDomain(url);
		if (domain && corsBlockedDomains.has(domain)) return '';
		// CORS ok or not yet checked — let <img> load directly
		return url;
	}

	// Rool URL not cached yet - return empty to prevent broken image
	return '';
}

/**
 * Preload media for a set of URLs.
 * Useful for batch loading images when the graph is rebuilt.
 */
export async function preloadMedia(urls: string[]): Promise<void> {
	const promises = urls
		.filter((url) => url && isRoolMediaUrl(url) && !blobUrlCache.has(url))
		.map((url) => resolveMediaUrl(url));

	await Promise.allSettled(promises);
}

// =============================================================================
// Per-domain CORS probe + proxy
// =============================================================================

/** Domains confirmed to support CORS (images load fine directly) */
const corsOkDomains = new Set<string>();

/** Domains confirmed to block CORS (need server proxy) */
const corsBlockedDomains = new Set<string>();

/** Pending CORS probe promises keyed by domain */
const pendingCorsProbes = new Map<string, Promise<boolean>>();

/**
 * Extract hostname from a URL, returning null for non-http URLs.
 */
function getDomain(url: string): string | null {
	try {
		const u = new URL(url);
		return u.hostname;
	} catch {
		return null;
	}
}

/**
 * Check whether a domain blocks CORS. Result is cached per domain.
 * Uses a HEAD request to minimize bandwidth.
 * Returns true if CORS is blocked (proxy needed).
 */
export async function isDomainCorsBlocked(url: string): Promise<boolean> {
	const domain = getDomain(url);
	if (!domain) return false;

	if (corsOkDomains.has(domain)) return false;
	if (corsBlockedDomains.has(domain)) return true;

	// Deduplicate probes for the same domain
	const pending = pendingCorsProbes.get(domain);
	if (pending !== undefined) return pending;

	const probe = (async (): Promise<boolean> => {
		try {
			const resp = await fetch(url, { method: 'HEAD', mode: 'cors' });
			if (resp.ok) {
				corsOkDomains.add(domain);
				return false;
			}
			// Non-ok (e.g. 404) — might still be CORS-ok but the URL is bad.
			// Treat as blocked so we try the proxy (which may follow redirects).
			corsBlockedDomains.add(domain);
			return true;
		} catch {
			corsBlockedDomains.add(domain);
			return true;
		} finally {
			pendingCorsProbes.delete(domain);
		}
	})();

	pendingCorsProbes.set(domain, probe);
	return probe;
}

/**
 * Proxy an external URL through the SDK's server-side proxy.
 * Called for URLs from CORS-blocked domains or on-demand when <img> fails.
 * Returns a blob URL on success, or null if the proxy also fails.
 */
export async function proxyMediaUrl(url: string): Promise<string | null> {
	if (!url || isRoolMediaUrl(url)) return null;
	if (url.startsWith('blob:') || url.startsWith('data:')) return null;

	// Already resolved via proxy
	const cached = blobUrlCache.get(url);
	if (cached) return cached;

	// Check for pending fetch
	const pending = pendingFetches.get(url);
	if (pending) return pending;

	const currentSpace = get(channel) as RoolChannel | null;
	if (!currentSpace) return null;

	const fetchPromise = (async (): Promise<string | null> => {
		try {
			const response = await currentSpace.fetchMedia(url);
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			blobUrlCache.set(url, blobUrl);
			return blobUrl;
		} catch {
			// Proxy also failed — image is genuinely unavailable
			return null;
		} finally {
			pendingFetches.delete(url);
		}
	})();

	pendingFetches.set(url, fetchPromise);
	return fetchPromise;
}

/**
 * Clear cached blob URLs (e.g., when space changes).
 * This revokes the blob URLs to free memory.
 */
export function clearMediaCache(): void {
	for (const blobUrl of blobUrlCache.values()) {
		URL.revokeObjectURL(blobUrl);
	}
	blobUrlCache.clear();
	corsOkDomains.clear();
	corsBlockedDomains.clear();
}

/**
 * Load an image and return its natural dimensions.
 * Uses fetchMedia for Rool SDK URLs.
 */
export async function getImageDimensions(
	url: string,
): Promise<{ width: number; height: number } | null> {
	const resolvedUrl = await resolveMediaUrl(url);
	if (!resolvedUrl) return null;

	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			resolve({
				width: img.naturalWidth,
				height: img.naturalHeight,
			});
		};
		img.onerror = () => {
			resolve(null);
		};
		img.src = resolvedUrl;
	});
}

/** Regex to find image URLs in markdown: ![alt](url) */
const MARKDOWN_IMAGE_REGEX = /!\[[^\]]*\]\(([^)]+)\)/g;

/** Regex to find URLs in HTML img tags: <img src="url" or <img src='url' */
const HTML_IMG_REGEX = /<img[^>]+src=["']([^"']+)["']/gi;

/**
 * Extract all image URLs from markdown content.
 */
export function extractImageUrlsFromMarkdown(content: string): string[] {
	const urls: string[] = [];

	// Find markdown image syntax: ![alt](url)
	let match;
	while ((match = MARKDOWN_IMAGE_REGEX.exec(content)) !== null) {
		urls.push(match[1]);
	}
	MARKDOWN_IMAGE_REGEX.lastIndex = 0; // Reset regex state

	// Find HTML img tags (in case markdown contains HTML)
	while ((match = HTML_IMG_REGEX.exec(content)) !== null) {
		urls.push(match[1]);
	}
	HTML_IMG_REGEX.lastIndex = 0; // Reset regex state

	return urls;
}

/**
 * Replace Rool media URLs in markdown content with cached blob URLs.
 * Returns the content with URLs replaced where cached, original URLs otherwise.
 */
export function replaceMediaUrlsInMarkdown(content: string): string {
	if (!content) return content;

	// Replace markdown image syntax: ![alt](url)
	let result = content.replace(MARKDOWN_IMAGE_REGEX, (match, url) => {
		const cached = blobUrlCache.get(url);
		if (cached) {
			return match.replace(url, cached);
		}
		// For Rool URLs without cache, use a placeholder to prevent broken images
		if (isRoolMediaUrl(url)) {
			// Return a transparent placeholder to avoid broken image
			return match.replace(
				url,
				'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
			);
		}
		return match;
	});

	// Replace HTML img tags
	result = result.replace(HTML_IMG_REGEX, (match, url) => {
		const cached = blobUrlCache.get(url);
		if (cached) {
			return match.replace(url, cached);
		}
		if (isRoolMediaUrl(url)) {
			return match.replace(
				url,
				'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
			);
		}
		return match;
	});

	return result;
}
